import { Platform } from 'react-native';
import { Timestamp } from '@firebase/firestore';
import { Structure } from '../types';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:structuresGeojson');

interface GeoJSONFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: {
    id: string;
    address: string | null;
    city: string;
    state: string;
    zip: string | null;
  };
}

interface GeoJSONFeatureCollection {
  type: 'FeatureCollection';
  serviceAreaId: string;
  shardId: string;
  features: GeoJSONFeature[];
}

export interface ShardManifestEntry {
  shardId: string;
  bbox: { minLat: number; maxLat: number; minLng: number; maxLng: number };
  count: number;
}

export interface AreaManifest {
  serviceAreaId: string;
  serviceAreaName: string;
  generatedAt: string;
  shardPrecision: number;
  totalStructures: number;
  shards: ShardManifestEntry[];
}

function resolveBaseUrl(): string {
  if (Platform.OS === 'web') return '';
  // Native: fetch from the hosted origin. Firebase Hosting maps
  // `{projectId}.firebaseapp.com` to the same content as `{projectId}.web.app`
  // (both serve the Expo `dist/` output).
  const origin = process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN;
  if (!origin) {
    throw new Error(
      'EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN must be set to fetch static structures on native.'
    );
  }
  return `https://${origin}`;
}

function indexUrl(serviceAreaId: string): string {
  return `${resolveBaseUrl()}/structures/${encodeURIComponent(serviceAreaId)}/index.json`;
}

function shardUrl(serviceAreaId: string, shardId: string): string {
  return `${resolveBaseUrl()}/structures/${encodeURIComponent(
    serviceAreaId
  )}/g/${encodeURIComponent(shardId)}.geojson`;
}

// Single shared Timestamp for all GeoJSON-sourced structures. The static
// export is immutable per manifest generation — every "createdAt"/"updatedAt"
// here is meaningless at the individual-structure level, so creating a fresh
// Timestamp per feature was pure allocation pressure. With SF at ~225k
// structures a batch, that was tens of thousands of wrapper objects per
// area switch. One shared reference keeps the Structure type happy without
// the GC churn.
const GEOJSON_EPOCH = Timestamp.now();

function featureToStructure(f: GeoJSONFeature): Structure {
  const [longitude, latitude] = f.geometry.coordinates;
  return {
    id: f.properties.id,
    centroid: { latitude, longitude },
    address: f.properties.address ?? undefined,
    city: f.properties.city,
    state: f.properties.state,
    zip: f.properties.zip ?? undefined,
    postsCount: 0,
    createdAt: GEOJSON_EPOCH,
    updatedAt: GEOJSON_EPOCH,
  };
}

export const structuresGeojsonApi = {
  /**
   * Fetch the shard manifest for a service area. Tiny (< 50KB for even the
   * densest area) — cheap to download on every area activation.
   */
  async loadAreaManifest(serviceAreaId: string, signal?: AbortSignal): Promise<AreaManifest> {
    const url = indexUrl(serviceAreaId);
    log.info(`Loading shard index for ${serviceAreaId}`);
    const res = await fetch(url, { signal });
    if (!res.ok) {
      throw new Error(
        `Failed to load shard index for ${serviceAreaId}: ${res.status} ${res.statusText}`
      );
    }
    const data = (await res.json()) as AreaManifest;
    if (!Array.isArray(data?.shards)) {
      throw new Error(`Invalid shard index at ${url}`);
    }
    return data;
  },

  /**
   * Fetch and parse a single geohash-prefix shard. Each shard is a small
   * GeoJSON FeatureCollection (sub-megabyte typically).
   */
  async loadShard(
    serviceAreaId: string,
    shardId: string,
    signal?: AbortSignal
  ): Promise<Structure[]> {
    const url = shardUrl(serviceAreaId, shardId);
    const res = await fetch(url, { signal });
    if (!res.ok) {
      throw new Error(
        `Failed to load shard ${serviceAreaId}/${shardId}: ${res.status} ${res.statusText}`
      );
    }
    const data = (await res.json()) as GeoJSONFeatureCollection;
    if (data?.type !== 'FeatureCollection' || !Array.isArray(data.features)) {
      throw new Error(`Invalid GeoJSON at ${url}`);
    }
    return data.features.map(featureToStructure);
  },
};

export function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err as { name?: string } | null)?.name === 'AbortError'
  );
}
